// src/pages/Users/Inscription/EditDocumentsModal.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import Button from "../../../components/Button/Button";
import {
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
  deleteDossierFile,
} from "../../../services/dossierServices";
import {
  FaTimes,
  FaSave,
  FaTrashAlt,
  FaPlusCircle,
  FaGoogleDrive,
  FaFilePdf,
  FaImage,
  FaLink,
  FaTimesCircle,
  FaFileMedical,
  FaFileSignature,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";

/* ---------------- Helpers ---------------- */
const SERVER_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/+$/, "");

const toAbsoluteUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const clean = u.startsWith("/") ? u : `/${u}`;
  return `${SERVER_BASE}${clean}`;
};

const isDrive = (u) =>
  !!u &&
  /https?:\/\/(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(
    u,
  );
const isImage = (u) =>
  !!u && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u.split("?")[0]);
const isPdf = (u) =>
  !!u && (u.toLowerCase().endsWith(".pdf") || u.toLowerCase().includes("pdf"));

/* Icône “état document” (gros bouton cliquable) */
const BigDocButton = ({ url, onClick }) => {
  const hasDocument = Boolean(url);

  if (!hasDocument) {
    return (
      <div
        className="
          flex min-h-[150px] w-full flex-col items-center justify-center
          rounded-2xl border-2 border-dashed border-slate-300
          bg-slate-50 px-4 py-6 text-center
          dark:border-slate-700 dark:bg-slate-900/40
        "
      >
        <div
          className="
            mb-3 flex h-16 w-16 items-center justify-center
            rounded-full bg-slate-200 text-slate-400
            dark:bg-slate-800 dark:text-slate-500
          "
        >
          <FaTimesCircle className="text-3xl" />
        </div>

        <p className="font-bold text-slate-700 dark:text-slate-200">
          Aucun document déposé
        </p>

        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Utilisez le bouton Ajouter pour téléverser un document.
        </p>
      </div>
    );
  }

  let documentType = "Document";

  if (isDrive(url)) {
    documentType = "Lien Google Drive";
  } else if (isPdf(url)) {
    documentType = "Document PDF";
  } else if (isImage(url)) {
    documentType = "Image";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="
        group relative flex min-h-[170px] w-full flex-col
        items-center justify-center overflow-hidden
        rounded-2xl border border-emerald-500
        bg-gradient-to-br from-emerald-500 to-emerald-600
        px-5 py-6 text-center shadow-sm
        transition
        hover:-translate-y-0.5 hover:shadow-lg
        focus:outline-none focus:ring-4 focus:ring-emerald-300/40
        dark:border-emerald-500
      "
      title="Ouvrir le document"
    >
      <div
        className="
          mb-3 flex h-20 w-20 items-center justify-center
          rounded-full bg-white/20 ring-4 ring-white/10
          transition-transform
          group-hover:scale-105
        "
      >
        <FaCheckCircle className="text-5xl text-white" />
      </div>

      <p className="text-base font-black text-white">Document déposé</p>

      <p className="mt-1 text-sm font-medium text-emerald-50">{documentType}</p>

      <div
        className="
          mt-3 rounded-full bg-white/15
          px-3 py-1 text-xs font-bold text-white
          transition group-hover:bg-white/25
        "
      >
        Cliquer pour consulter
      </div>
    </button>
  );
};

/* Cadre section générique (sans badge de validation) */
const SectionCard = ({ title, icon, children }) => (
  <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/70 p-4">
    <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
      <span className="text-base sm:text-lg">{icon}</span>
      <h4 className="font-semibold text-sm sm:text-base">{title}</h4>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

/* ---------------- Main Modal ---------------- */
const EditDocumentsModal = ({ isOpen, onClose, dossier, refreshDossiers }) => {
  // Hooks en haut, sans condition
  const [saving, setSaving] = useState(false);

  // États pour afficher/masquer les formulaires (fermés par défaut)
  const [showFormCM, setShowFormCM] = useState(false);
  const [showFormSig, setShowFormSig] = useState(false);
  const [showFormLic, setShowFormLic] = useState(false);

  // Modes & inputs pour chaque section
  const [modeCM, setModeCM] = useState("upload");
  const [fileCM, setFileCM] = useState(null);
  const [urlCM, setUrlCM] = useState("");
  const [dateCM, setDateCM] = useState(""); // indépendant

  const [modeSig, setModeSig] = useState("upload");
  const [fileSig, setFileSig] = useState(null);
  const [urlSig, setUrlSig] = useState("");

  const [modeLic, setModeLic] = useState("upload");
  const [fileLic, setFileLic] = useState(null);
  const [urlLic, setUrlLic] = useState("");

  const dateInputRef = useRef(null);

  // Valeurs actuelles (compat noms de colonnes)
  const currentCM = dossier?.cm_url || "";
  const currentSignee =
    dossier?.formulaire_licence_signe ||
    dossier?.formulaire_licence_signee ||
    "";
  const currentLicence =
    dossier?.licence_url ||
    dossier?.licence_assurance_url ||
    dossier?.licence_assurance ||
    "";

  // (Ré)initialisation à l’ouverture
  useEffect(() => {
    if (!isOpen || !dossier?.id) return;

    setShowFormCM(false);
    setShowFormSig(false);
    setShowFormLic(false);

    // Init modes/inputs avec les valeurs actuelles
    setModeCM(
      currentCM && currentCM.startsWith("/uploads/")
        ? "upload"
        : currentCM
          ? "link"
          : "upload",
    );
    setFileCM(null);
    setUrlCM(currentCM || "");
    setDateCM(toInputDateSafe(dossier?.date_cm)); // valeur indépendante

    setModeSig(
      currentSignee && currentSignee.startsWith("/uploads/")
        ? "upload"
        : currentSignee
          ? "link"
          : "upload",
    );
    setFileSig(null);
    setUrlSig(currentSignee || "");

    setModeLic(
      currentLicence && currentLicence.startsWith("/uploads/")
        ? "upload"
        : currentLicence
          ? "link"
          : "upload",
    );
    setFileLic(null);
    setUrlLic(currentLicence || "");

    setSaving(false);
  }, [isOpen, dossier?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !dossier?.id) return null;

  /* ---------- Actions génériques ---------- */
  const openUrl = (u) => {
    if (!u) return;
    window.open(toAbsoluteUrl(u), "_blank", "noopener,noreferrer");
  };

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;
    try {
      setSaving(true);
      await deleteDossierFile(dossier.id, field);
      toast.success(`${label} supprimé.`);
      await refreshDossiers?.();
    } catch (e) {
      console.error(e);
      toast.error(`Échec de la suppression de « ${label} ».`);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Sauvegardes par section ---------- */

  // 1) Médical — Document (fichier OU lien) — la date n’est plus obligatoire ici
  const saveCM = async () => {
    if (modeCM === "upload" && !fileCM) {
      toast.error("Choisissez un fichier (PDF ou image).");
      return;
    }
    if (modeCM === "link" && urlCM && !/^https?:\/\//i.test(urlCM)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);
    try {
      if (modeCM === "upload") {
        // envoi du fichier; si tu veux pousser la date avec, ajoute { date_cm: dateCM || undefined }
        await uploadCertificatMedical(
          dossier.id,
          fileCM,
          dateCM ? { date_cm: dateCM } : undefined,
        );
      } else {
        if (currentCM?.startsWith?.("/uploads/") && urlCM) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch {}
        }
        const payload = { cm_url: urlCM || null };
        if (dateCM) payload.date_cm = dateCM; // inclure la date seulement si saisie
        await updateDossierInscription(dossier.id, payload);
      }
      await refreshDossiers?.();
      setShowFormCM(false);
      toast.success("Document médical mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement du document médical.");
    } finally {
      setSaving(false);
    }
  };

  // 1-bis) Médical — Sauvegarde indépendante de la date
  const saveCMDate = async () => {
    if (!dateCM) {
      toast.error("Indiquez la date du certificat/attestation.");
      if (dateInputRef.current) dateInputRef.current.focus();
      return;
    }
    setSaving(true);
    try {
      await updateDossierInscription(dossier.id, { date_cm: dateCM });
      await refreshDossiers?.();
      toast.success("Date du document médical enregistrée.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement de la date.");
    } finally {
      setSaving(false);
    }
  };

  // 2) Formulaire signé
  const saveSignee = async () => {
    if (modeSig === "upload" && !fileSig) {
      toast.error("Choisissez un fichier (PDF ou image).");
      return;
    }
    if (modeSig === "link" && urlSig && !/^https?:\/\//i.test(urlSig)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);
    try {
      if (modeSig === "upload") {
        await uploadFormulaireLicenceSigne(dossier.id, fileSig);
      } else {
        if (currentSignee?.startsWith?.("/uploads/") && urlSig) {
          try {
            await deleteDossierFile(dossier.id, "formulaire_licence_signe");
          } catch {}
        }
        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: urlSig || null,
          formulaire_licence_signee: urlSig || null,
        });
      }
      await refreshDossiers?.();
      setShowFormSig(false);
      toast.success("Formulaire signé mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // 3) Licence (optionnelle)
  const saveLicence = async () => {
    if (modeLic === "upload" && !fileLic) {
      toast.error("Choisissez un fichier (PDF ou image).");
      return;
    }
    if (modeLic === "link" && urlLic && !/^https?:\/\//i.test(urlLic)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);
    try {
      if (modeLic === "upload") {
        await uploadLicenceDefinitive(dossier.id, fileLic);
      } else {
        if (currentLicence?.startsWith?.("/uploads/") && urlLic) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch {}
        }
        await updateDossierInscription(dossier.id, {
          licence_url: urlLic || null,
          licence_assurance_url: urlLic || null,
          licence_assurance: urlLic || null,
        });
      }
      await refreshDossiers?.();
      setShowFormLic(false);
      toast.success("Licence / Assurance mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // Prend 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ', Date, etc. et renvoie 'YYYY-MM-DD'
  const toInputDateSafe = (v) => {
    if (!v) return "";
    if (typeof v === "string") {
      const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  /* ---------- UI ---------- */

  const Header = (
    <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
      <div>
        <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
          Documents d’inscription
        </h3>
        <p className="mt-1 text-white/90 text-[11px] sm:text-xs">
          Déposez les documents nécessaires au dossier d'inscription. Les
          documents enregistrés apparaissent en vert.
        </p>
      </div>
      <button
        onClick={() => !saving && onClose?.()}
        className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
        aria-label="Fermer"
        type="button"
        title="Fermer"
      >
        <FaTimes />
      </button>
    </div>
  );

  const FormUploadOrLink = ({
    mode,
    setMode,
    file,
    setFile,
    url,
    setUrl,
    extraBelow,
  }) => (
    <div className="mt-3 space-y-4">
      {/* Choix du mode */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`
          rounded-xl border px-3 py-3 text-sm font-bold transition
          ${
            mode === "upload"
              ? "border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/10 dark:bg-blue-500/10 dark:text-blue-300"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
          }
        `}
        >
          <div className="flex items-center justify-center gap-2">
            <FaPlusCircle />
            Téléverser un fichier
          </div>
        </button>

        <button
          type="button"
          onClick={() => setMode("link")}
          className={`
          rounded-xl border px-3 py-3 text-sm font-bold transition
          ${
            mode === "link"
              ? "border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/10 dark:bg-blue-500/10 dark:text-blue-300"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
          }
        `}
        >
          <div className="flex items-center justify-center gap-2">
            <FaGoogleDrive />
            Coller un lien
          </div>
        </button>
      </div>

      {/* Upload fichier */}
      {mode === "upload" ? (
        <div className="space-y-3">
          <label
            className="
            flex cursor-pointer flex-col items-center justify-center
            rounded-2xl border-2 border-dashed border-slate-300
            bg-slate-50 px-4 py-6 text-center
            transition
            hover:border-blue-400 hover:bg-blue-50/50
            dark:border-slate-700 dark:bg-slate-900/50
            dark:hover:border-blue-500 dark:hover:bg-blue-500/5
          "
          >
            <FaPlusCircle className="mb-2 text-3xl text-blue-500" />

            <span className="font-bold text-slate-800 dark:text-white">
              Choisir un document
            </span>

            <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              PDF ou image
            </span>

            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              className="hidden"
            />
          </label>

          {/* Fichier sélectionné */}
          {file && (
            <div
              className="
              flex items-center gap-3
              rounded-2xl border border-emerald-300
              bg-emerald-50 p-4
              dark:border-emerald-800 dark:bg-emerald-950/30
            "
            >
              <div
                className="
                flex h-12 w-12 shrink-0 items-center justify-center
                rounded-full bg-emerald-500
              "
              >
                <FaCheckCircle className="text-2xl text-white" />
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-bold text-emerald-800 dark:text-emerald-300">
                  Fichier prêt à être envoyé
                </p>

                <p
                  className="
                  mt-0.5 truncate text-sm
                  text-emerald-700 dark:text-emerald-400
                "
                  title={file.name}
                >
                  {file.name}
                </p>

                <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-500">
                  {(file.size / 1024 / 1024).toFixed(2)} Mo
                </p>
              </div>

              <button
                type="button"
                onClick={() => setFile(null)}
                className="
                flex h-9 w-9 shrink-0 items-center justify-center
                rounded-xl text-emerald-700
                transition hover:bg-emerald-100
                dark:text-emerald-300 dark:hover:bg-emerald-900
              "
                title="Retirer le fichier"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Lien */
        <div>
          <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
            Lien vers le document
          </label>

          <div className="relative">
            <FaLink
              className="
              absolute left-3 top-1/2
              -translate-y-1/2
              text-slate-400
            "
            />

            <input
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://drive.google.com/..."
              className="
              w-full rounded-xl border border-slate-300
              bg-white py-3 pl-10 pr-3 text-sm
              text-slate-900 outline-none
              transition
              focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
              dark:border-slate-700 dark:bg-slate-950 dark:text-white
            "
            />
          </div>

          {url && /^https?:\/\//i.test(url) && (
            <div
              className="
              mt-3 flex items-center gap-3
              rounded-xl border border-emerald-300
              bg-emerald-50 p-3
              dark:border-emerald-800 dark:bg-emerald-950/30
            "
            >
              <div
                className="
                flex h-9 w-9 items-center justify-center
                rounded-full bg-emerald-500
              "
              >
                <FaCheckCircle className="text-white" />
              </div>

              <div>
                <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
                  Lien renseigné
                </p>

                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  Le document pourra être enregistré.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {extraBelow}
    </div>
  );

  // Petites classes utilitaires pour boutons compacts
  const btnSmall = "!rounded-lg !px-2.5 !py-1.5 !text-xs";
  const btnDangerSmall = `${btnSmall} !bg-rose-600 hover:!bg-rose-700`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose?.()}
    >
      <div
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {Header}

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* Médical */}
          <SectionCard
            title="Médical (certificat / attestation)"
            icon={<FaFileMedical />}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <BigDocButton
                url={currentCM}
                onClick={() => openUrl(currentCM)}
              />
              {/* Boutons compacts, sur une seule ligne */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentCM ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormCM((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />
                {currentCM && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() => removeField("cm_url", "Document médical")}
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {/* Saisie indépendante de la date (SOUS les boutons) */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <label className="block text-[13px] font-medium mb-1">
                  Date figurant sur le document
                </label>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateCM}
                  onChange={(e) => setDateCM(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[12px] text-slate-500">
                  Vous pouvez enregistrer la date indépendamment du
                  fichier/lien.
                </p>
              </div>
              <Button
                type="button"
                text={saving ? "Enregistrement…" : "Enregistrer la date"}
                icon={FaSave}
                onClick={saveCMDate}
                disabled={saving}
                className={`${btnSmall} gap-2`}
              />
            </div>

            {/* Formulaire fichier/lien (affiché à la demande) */}
            {showFormCM && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <FormUploadOrLink
                  mode={modeCM}
                  setMode={setModeCM}
                  file={fileCM}
                  setFile={setFileCM}
                  url={urlCM}
                  setUrl={setUrlCM}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={
                          saving ? "Enregistrement…" : "Enregistrer le document"
                        }
                        icon={FaSave}
                        onClick={saveCM}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />
                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormCM(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Formulaire signé */}
          <SectionCard
            title="Formulaire de licence signé"
            icon={<FaFileSignature />}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <BigDocButton
                url={currentSignee}
                onClick={() => openUrl(currentSignee)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentSignee ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormSig((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />
                {currentSignee && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() =>
                      removeField(
                        "formulaire_licence_signe",
                        "Formulaire signé",
                      )
                    }
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {showFormSig && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <FormUploadOrLink
                  mode={modeSig}
                  setMode={setModeSig}
                  file={fileSig}
                  setFile={setFileSig}
                  url={urlSig}
                  setUrl={setUrlSig}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={saving ? "Enregistrement…" : "Enregistrer"}
                        icon={FaSave}
                        onClick={saveSignee}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />
                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormSig(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Licence (optionnelle) */}
          <SectionCard
            title="Licence / Assurance définitive (optionnel, reçue par mail)"
            icon={<FaCheckCircle />}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <BigDocButton
                url={currentLicence}
                onClick={() => openUrl(currentLicence)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentLicence ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormLic((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />
                {currentLicence && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() =>
                      removeField("licence_url", "Licence / Assurance")
                    }
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {showFormLic && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <FormUploadOrLink
                  mode={modeLic}
                  setMode={setModeLic}
                  file={fileLic}
                  setFile={setFileLic}
                  url={urlLic}
                  setUrl={setUrlLic}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={saving ? "Enregistrement…" : "Enregistrer"}
                        icon={FaSave}
                        onClick={saveLicence}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />
                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormLic(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EditDocumentsModal;
